/**
 * Prediction History Page - SolarPredict
 * 
 * This page displays the complete history of predictions for the connected user
 * in a simple table format with columns: Model | String | Created At
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPredictionHistory, deletePredictionResult as deletePrediction, getAllUserPredictions, getPredictionChartData } from '../api/prediction';
import useAuth from '../hooks/useAuth';
import { FaSearch, FaEye, FaTrash, FaCalendarAlt, FaSolarPanel, FaBrain, FaFilter, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import PredictionIscChart from './charts/PredictionIscChart';
import PredictionVocChart from './charts/PredictionVocChart';
import PredictionVmppChart from './charts/PredictionVmppChart';
import PredictionImppChart from './charts/PredictionImppChart';
import PredictionPmppChart from './charts/PredictionPmppChart';

const History = () => {
  // Local states
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPrediction, setSelectedPrediction] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    model: '',
    string: '',
    date: ''
  });
  
  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10
  });
  
  // Get authentication token from context
  const { currentUser } = useAuth();
  
  // Get token from localStorage
  const token = localStorage.getItem('token');
  
  // Debug: Log component state
  console.log('🔄 History component rendered with:', { 
    loading, 
    error, 
    predictions: predictions.length, 
    currentUser, 
    token: !!token 
  });

  /**
   * Récupération de l'historique des prédictions depuis le serveur
   */
  useEffect(() => {
    const fetchPredictions = async () => {
      setLoading(true);
      
      // Debug: Check authentication state
      console.log('🔐 Auth state:', { currentUser, token: !!token });
      
      if (!token) {
        console.log('❌ No token found, cannot fetch predictions');
        setError('You must be logged in to view your history');
        setLoading(false);
        return;
      }
      
      try {
        console.log('🚀 Fetching user predictions...');
        const response = await getAllUserPredictions();
        
        if (response.success) {
          console.log('✅ User predictions received:', response);
          console.log(`📊 Total predictions: ${response.total_predictions}`);
          
          setPredictions(response.data);
          
          setPagination(prev => ({
            ...prev,
            totalPages: Math.ceil(response.data.length / prev.itemsPerPage)
          }));
        } else {
          setError('Error loading history');
        }
      } catch (err) {
        console.error('Erreur:', err);
        setError('Unable to load your prediction history');
      } finally {
        setLoading(false);
      }
    };

    fetchPredictions();
  }, [token, currentUser]);

  /**
   * Handle prediction deletion
   */
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this prediction?')) {
      try {
        const response = await deletePrediction(id);
        
        if (response.success) {
          setPredictions(predictions.filter(pred => pred.id !== id));
        } else {
          setError('Error during deletion');
        }
      } catch (err) {
        console.error('Erreur:', err);
        setError('Unable to delete this prediction');
      }
    }
  };

  /**
   * Handle view button click - display prediction info and chart
   */
  const handleView = async (predictionId) => {
    console.log(`🔍 View button clicked for prediction ID: ${predictionId}`);
    
    // Find the prediction in the list
    const prediction = predictions.find(p => p.id === predictionId);
    
    if (prediction) {
      console.log(`👤 User ID from database: ${prediction.user_id}`);
      setSelectedPrediction(prediction);
      
      // Fetch chart data for this prediction
      setChartLoading(true);
      setChartData(null);
      
      try {
        console.log(`📊 Fetching chart data for prediction ${predictionId}...`);
        const response = await getPredictionChartData(predictionId);
        
        if (response.success) {
          console.log('✅ Chart data received:', response);
          setChartData(response.chart_data);
        } else {
          console.error('Failed to fetch chart data:', response.message);
          setError('Failed to load chart data');
        }
      } catch (err) {
        console.error('Error fetching chart data:', err);
        setError('Unable to load chart data for this prediction');
      } finally {
        setChartLoading(false);
      }
    }
  };

  /**
   * Handle filter changes
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  /**
   * Apply filters to the prediction list
   */
  const filteredPredictions = predictions.filter(pred => {
    const matchesSearch = !filters.search || 
      pred.id.toString().includes(filters.search) ||
      pred.model.toString().includes(filters.search) ||
      pred.string.toString().includes(filters.search) ||
      pred.created_at.includes(filters.search);
    
    const matchesModel = !filters.model || pred.model.toString() === filters.model;
    const matchesString = !filters.string || pred.string.toString() === filters.string;
    const matchesDate = !filters.date || pred.created_at.includes(filters.date);
    
    return matchesSearch && matchesModel && matchesString && matchesDate;
  });

  /**
   * Calculate paginated predictions
   */
  const paginatedPredictions = filteredPredictions.slice(
    (pagination.currentPage - 1) * pagination.itemsPerPage,
    pagination.currentPage * pagination.itemsPerPage
  );

  /**
   * Handle page change
   */
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  /**
   * Calculate unique values for filters
   */
  const uniqueModels = [...new Set(predictions.map(p => p.model))];
  const uniqueStrings = [...new Set(predictions.map(p => p.string))];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-green-50 py-8">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 2xl:px-16">
        
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">
            📊 Prediction History
          </h1>
          <p className="text-lg text-gray-600">
            View all your past solar predictions
          </p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <FaFilter className="text-green-500 text-xl" />
            <h3 className="text-xl font-semibold text-gray-800">Filter</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="space-y-2">
              <label className="block text-base font-semibold text-gray-700">Search</label>
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Search predictions..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="block text-base font-semibold text-gray-700">Model</label>
              <select
                name="model"
                value={filters.model}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
              >
                <option value="">All models</option>
                {uniqueModels.map(model => (
                  <option key={model} value={model}>Model {model}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="block text-base font-semibold text-gray-700">String</label>
              <select
                name="string"
                value={filters.string}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
              >
                <option value="">All strings</option>
                {uniqueStrings.map(string => (
                  <option key={string} value={string}>String {string}</option>
                ))}
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="block text-base font-semibold text-gray-700">Date</label>
              <input
                type="date"
                name="date"
                value={filters.date}
                onChange={handleFilterChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200"
              />
            </div>
          </div>
        </div>

   

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent mx-auto mb-4"></div>
              <p className="text-lg text-gray-600 font-medium">Loading your history...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-6 mb-6 rounded-xl">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <span className="text-red-600 text-lg">⚠️</span>
              </div>
              <div>
                <p className="font-semibold">Loading Error</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          </div>
        ) : filteredPredictions.length === 0 ? (
          <div className="bg-white p-16 rounded-2xl shadow-xl border border-gray-100 text-center">
            <div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">No predictions found</h3>
            <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
              It looks like you haven't created any predictions yet. Start by creating your first solar prediction!
            </p>
            <Link 
              to="/predict"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-green-500 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-green-600 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create my first prediction
            </Link>
          </div>
        ) : (
          <>
            {/* Simple Table Layout */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-green-500">
                <h3 className="text-xl font-bold text-white">Your Predictions ({filteredPredictions.length})</h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Model</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">String</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedPredictions.map(prediction => (
                      <tr key={prediction.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {prediction.model}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {prediction.string}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(prediction.created_at).toLocaleString('en-US')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleView(prediction.id)}
                              className="text-blue-600 hover:text-blue-900 px-3 py-1 rounded-md hover:bg-blue-50"
                            >
                              View
                            </button>
                            <button
                              onClick={() => handleDelete(prediction.id)}
                              className="text-red-600 hover:text-red-900 px-3 py-1 rounded-md hover:bg-red-50"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Selected Prediction Info */}
            {selectedPrediction && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mt-8">
                <div className="flex items-center space-x-3 mb-4">
                  <FaEye className="text-blue-500 text-xl" />
                  <h3 className="text-xl font-semibold text-gray-800">Prediction Information</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-800 mb-2">User ID</h4>
                    <p className="text-2xl font-bold text-blue-900">{selectedPrediction.user_id}</p>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-green-800 mb-2">Prediction ID</h4>
                    <p className="text-2xl font-bold text-green-900">{selectedPrediction.id}</p>
                  </div>
                  
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-purple-800 mb-2">Model</h4>
                    <p className="text-2xl font-bold text-purple-900">{selectedPrediction.model}</p>
                  </div>
                  
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-orange-800 mb-2">String</h4>
                    <p className="text-2xl font-bold text-orange-900">{selectedPrediction.string}</p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg md:col-span-2">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Created At</h4>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(selectedPrediction.created_at).toLocaleString('en-US')}
                    </p>
                  </div>
                </div>
                
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={() => {
                      setSelectedPrediction(null);
                      setChartData(null);
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Chart Section - Styled like Prediction Area */}
            {selectedPrediction && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 mt-8">
                <div className="flex items-center space-x-3 mb-6">
                  <FaSolarPanel className="text-orange-500 text-xl" />
                  <h3 className="text-xl font-semibold text-gray-800">Prediction Results</h3>
                </div>
                
                <div className="mb-4">
                  <p className="text-gray-600 mb-4">
                    Historical prediction data visualization from database.
                  </p>
                </div>
                
                {/* Chart Grid - Wider layout for better chart visibility */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8">
                  {/* ISC Chart */}
                  <div className="bg-white rounded-xl p-6 border shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6">
                      Predicted short circuit current (I<sub className="italic lowercase">sc</sub>)
                    </h3>
                    <PredictionIscChart 
                      chartData={chartData} 
                      loading={chartLoading} 
                    />
                  </div>
                  
                  {/* VOC Chart */}
                  <div className="bg-white rounded-xl p-6 border shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6">
                      Predicted open circuit voltage (V<sub className="italic lowercase">oc</sub>)
                    </h3>
                    <PredictionVocChart 
                      chartData={chartData} 
                      loading={chartLoading} 
                    />
                  </div>
                  
                  {/* VMPP Chart */}
                  <div className="bg-white rounded-xl p-6 border shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6">
                      Predicted maximum power point voltage (V<sub className="italic lowercase">mpp</sub>)
                    </h3>
                    <PredictionVmppChart 
                      chartData={chartData} 
                      loading={chartLoading} 
                    />
                  </div>
                  
                  {/* IMPP Chart */}
                  <div className="bg-white rounded-xl p-6 border shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6">
                      Predicted maximum power point current (I<sub className="italic lowercase">mpp</sub>)
                    </h3>
                    <PredictionImppChart 
                      chartData={chartData} 
                      loading={chartLoading} 
                    />
                  </div>
                  
                  {/* PMPP Chart */}
                  <div className="bg-white rounded-xl p-6 border shadow-lg hover:shadow-xl transition-shadow">
                    <h3 className="text-lg font-semibold text-gray-800 mb-6">
                      Predicted maximum power point (P<sub className="italic lowercase">mpp</sub>)
                    </h3>
                    <PredictionPmppChart 
                      chartData={chartData} 
                      loading={chartLoading} 
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Pagination */}
            {filteredPredictions.length > pagination.itemsPerPage && (
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 px-6 py-4 mt-8">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <p className="text-sm text-gray-600">
                      Showing{' '}
                      <span className="font-semibold text-gray-800">
                        {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}
                      </span>
                      {' '}to{' '}
                      <span className="font-semibold text-gray-800">
                        {Math.min(pagination.currentPage * pagination.itemsPerPage, filteredPredictions.length)}
                      </span>
                      {' '}of{' '}
                      <span className="font-semibold text-gray-800">{filteredPredictions.length}</span>
                      {' '}results
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(Math.max(1, pagination.currentPage - 1))}
                      disabled={pagination.currentPage === 1}
                      className={`p-2 rounded-xl transition-all duration-200 ${
                        pagination.currentPage === 1 
                          ? 'text-gray-300 cursor-not-allowed' 
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <FaChevronLeft className="h-5 w-5" />
                    </button>
                    
                    <div className="flex items-center space-x-1">
                      {[...Array(pagination.totalPages)].map((_, i) => {
                        const pageNumber = i + 1;
                        return (
                          <button
                            key={pageNumber}
                            onClick={() => handlePageChange(pageNumber)}
                            className={`px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                              pageNumber === pagination.currentPage
                                ? 'bg-gradient-to-r from-blue-500 to-green-500 text-white shadow-lg'
                                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                            }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}
                    </div>
                    
                    <button
                      onClick={() => handlePageChange(Math.min(pagination.totalPages, pagination.currentPage + 1))}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className={`p-2 rounded-xl transition-all duration-200 ${
                        pagination.currentPage === pagination.totalPages
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
                      }`}
                    >
                      <FaChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default History;
